import { initializeApp } from "firebase/app";
import { 
    getAuth, 
    GoogleAuthProvider, 
    signInWithPopup, 
    signOut, 
    onAuthStateChanged,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    sendPasswordResetEmail,
    updateProfile,
} from "firebase/auth";
import {
    doc,
    getDoc,
    setDoc,
    getDocs,
    collection,
    runTransaction
} from "firebase/firestore";
import { getFirestore } from "firebase/firestore";
import { useState, useEffect } from "react";
import Toast from "../lib/toast";
import { SUBMISSION_ID_KEY, EDITED_AT_KEY } from "./submissionId";

// Firebase config
const firebaseConfig = {
    apiKey: "AIzaSyCaPPz9_e7c2Oad37PTIb7XHbezqk27RQk",
    authDomain: "escouting-81439.firebaseapp.com",
    projectId: "escouting-81439",
    storageBucket: "escouting-81439.appspot.com",
    messagingSenderId: "687238956862",
    appId: "1:687238956862:web:c708d3595c58a027024e6f"
};

// initialize firebase
const app = initializeApp(firebaseConfig);
export const auth = getAuth(app); 
export const db = getFirestore(app);

// google authentication provider
const googleProvider = new GoogleAuthProvider();


export const useAuth = () => {
    const [user, setUser] = useState(null);
    const [authentication, setAuthentication] = useState(false);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            setUser(currentUser);
            if (currentUser) {
                await checkAuth(2025, setAuthentication);
            } else {
                setAuthentication(false);
            }
        });

        return () => unsubscribe();
    }, []);

    return { user, authentication, setUser, setAuthentication };
};

/**
 * Checks if the current signed in user has firebase authorization
 * @param {int} year 
 * @param {} setAuthentication 
 */
export async function checkAuth(year, setAuthentication){
    const yearConfigRef = doc(db, `Competitions/Years/${year}/config`);
    try {
        const yearConfig = await getDoc(yearConfigRef);
        setAuthentication(yearConfig.exists());
        console.log("is authed")
        return true;
    } catch (error) {
        console.error("Error checking authorization:", error);
        setAuthentication(false);
        return false;
    }
};

/**
 * Uses google sign in
 * @param {*} setAuthentication 
 * @returns 
 */
export const signInWithGoogle = async (setAuthentication) => {
    try {
        // Force Google to show the account chooser every time
        googleProvider.setCustomParameters({ prompt: "select_account" });
        const newuser = await signInWithPopup(auth, googleProvider);
        return newuser;
    } catch (error) {
        console.error("Google sign-in error:", error.message);
    }
};

/**
 * Signs in with email and password
 * @param {string} email
 * @param {string} password
 * @param {*} setUser
 * @param {*} setAuthentication
 * @returns {boolean} success
 */
export const signInWithEmail = async (email, password, setUser, setAuthentication) => {
    if (navigator.onLine === false) {
        Toast.error("No internet connection detected. Please connect to a Wi-Fi or cellular network to sign in.");
        return false;
    }
    try {
        const result = await signInWithEmailAndPassword(auth, email, password);
        const outcome = await checkAuth(2025, setAuthentication);
        if (!outcome) {
            Toast.error("Account Not Authorized. Sign in failed.");
            await signOut(auth);
            setUser(null);
            setAuthentication(false);
            return false;
        }
        setUser(result.user);
        Toast.success("Signed in! Welcome, " + (result.user.displayName || result.user.email));
        return true;
    } catch (error) {
        console.error("Email sign-in error:", error.code, error.message);
        let msg = "Sign in failed. Please try again.";
        if (error.code === "auth/user-not-found" || error.code === "auth/wrong-password" || error.code === "auth/invalid-credential") {
            msg = "Incorrect email or password.";
        } else if (error.code === "auth/invalid-email") {
            msg = "Invalid email address.";
        } else if (error.code === "auth/too-many-requests") {
            msg = "Too many attempts. Please try again later.";
        }
        Toast.error(msg);
        return false;
    }
};

/**
 * Creates a new account with email and password
 * @param {string} email
 * @param {string} password
 * @param {string} displayName
 * @param {*} setUser
 * @param {*} setAuthentication
 * @returns {boolean} success
 */
export const createEmailAccount = async (email, password, displayName, setUser, setAuthentication) => {
    if (navigator.onLine === false) {
        Toast.error("No internet connection detected.");
        return false;
    }

    // Check if email is allowed BEFORE creating the account
    try {
        const docSnap = await getDoc(doc(db, 'Allowed_Emails', email));
        if (!docSnap.exists()) {
            Toast.error("This email is not authorized to create an account.");
            return false;
        }
    } catch (error) {
        Toast.error("Could not verify email authorization.");
        return false;
    }

    try {
        const result = await createUserWithEmailAndPassword(auth, email, password);
        if (displayName && displayName.trim() !== "") {
            await updateProfile(result.user, { displayName: displayName.trim() });
        }
        setUser(result.user);
        setAuthentication(true);
        Toast.success("Account created! Welcome, " + (displayName || email));
        return true;
    } catch (error) {
        console.error("Account creation error:", error.code, error.message);
        let msg = "Account creation failed. Please try again.";
        if (error.code === "auth/email-already-in-use") msg = "An account with this email already exists.";
        else if (error.code === "auth/invalid-email") msg = "Invalid email address.";
        else if (error.code === "auth/weak-password") msg = "Password should be at least 6 characters.";
        Toast.error(msg);
        return false;
    }
};

let currentSignInAttempt = 0;
/**
 * Signs user in and performs proper checks
 * @param {*} setUser 
 * @param {*} setCurrentTab 
 * @param {*} setAuthentication 
 */
export const signIn = async (setUser, setCurrentTab, setAuthentication) => {
    if(navigator.onLine === false) {
        Toast.error("No internet connection detected. Please connect to a Wi-Fi or cellular network to sign in.")
        logout(setUser,setAuthentication);
        return;
    }
    currentSignInAttempt++;
    const attempt = currentSignInAttempt;
    let result;
    try {
        result = await signInWithGoogle(setAuthentication);
    } catch (error) {
        if (attempt !== currentSignInAttempt) return;
        console.error("Sign-in error:", error.message);
        logout(setUser,setAuthentication);
        Toast.error("Sign in failed. Please try again");
        return;
    }

    if (attempt !== currentSignInAttempt) return;
    try {
        const outcome = await checkAuth(2025, setAuthentication);
        if(!outcome){
            Toast.error("Account Not Authorized. Sign in failed.");
            logout(setUser,setAuthentication);
            return;
        }
        const loggedUser = result.user;
        setUser(loggedUser);
        console.log("Username: " + loggedUser.displayName);
        Toast.success("Signed in! Welcome, " + loggedUser.displayName)
    } catch (error) {
        if (attempt !== currentSignInAttempt) return;
        console.error("Sign-in error:", error.message);
        logout(setUser,setAuthentication);
        Toast.error("Sign in failed. Please try again :)");
    }
};

/**
 * Logs user out
 * @param {*} setUser 
 * @param {*} setAuthentication 
 */
export const logout = async (setUser, setAuthentication) => {
    try {
        await signOut(auth);
        console.log("User signed out");
        setUser(null);
        setAuthentication(false);
    } catch (error) {
        console.error("Error signing out:", error.message);
        Toast.error("Error signing out")
    }
};

/**
 * Uploads the object to firebase, ONLY RUN IF OBJECT HAS BEEN VERIFIED FIRST
 * @param {*} dataObject 
 * @param {*} eventName 
 * @param {int} gamemode 0 for prescout, 1 for practice, 2 for quals, 3 for elims
 * @returns 
 */
/**
 * Write one match record to its slot in Firebase.
 *
 * `onlyIfAbsent`: an unedited copy - a saved match re-uploaded, a scanned QR - never
 * overwrites a slot that is already there, so it cannot undo a fix someone made in
 * Firebase. The read and the write are one transaction. An edited copy, and a fresh
 * Submit, overwrite as before: an edit is a correction, wherever it is made.
 * @returns {Promise<boolean|'exists'>} true when written, 'exists' when left alone
 */
export async function uploadObject(dataObject, eventName, gamemode, { quiet = false, onlyIfAbsent = false } = {}) {

    delete dataObject["version"]; //very importnat
    delete dataObject[SUBMISSION_ID_KEY]; // for the Pi only - Neptune would show it as a column
    delete dataObject[EDITED_AT_KEY];
    delete dataObject["_edited_at"];
    const year = eventName.substring(0, 4);
    const eventCode = eventName.substring(4);
    const matchKey = "" + (parseInt(dataObject["match_number"]) + (gamemode + 1) * 1000) + "-" + dataObject["alliance_color"] //MAYBE ADD BACK ROBOT POSITION HERE
    const id = dataObject["scout_team"] + "-" + dataObject["scout_id"] + "-" + dataObject["alliance_color"] + dataObject["team_position"];

    try {
        if(2025 === parseInt(year) || 9999 === parseInt(year)){
            const unsortedDataRef = collection(
                db,
                "Competitions",
                "Years",
                year,
                eventCode,
                "Final",
                matchKey,
                "Unsorted"
            );
            const slotRef = doc(unsortedDataRef, id);
            if (onlyIfAbsent) {
                const written = await runTransaction(db, async (tx) => {
                    if ((await tx.get(slotRef)).exists()) return false;
                    tx.set(slotRef, dataObject);
                    return true;
                });
                if (!written) return alreadyThere(quiet);
            } else {
                await setDoc(slotRef, dataObject);
            }
            
        } else{
            const docRef = doc(
                db,
                "Competitions",
                "Years",
                year,
                eventCode,
                "Final",
                matchKey
            );
            if (onlyIfAbsent) {
                const written = await runTransaction(db, async (tx) => {
                    const snap = await tx.get(docRef);
                    if (snap.exists() && snap.data()?.normal_data?.[id] !== undefined) return false;
                    tx.set(docRef, { normal_data: { [id]: dataObject } }, { merge: true });
                    return true;
                });
                if (!written) return alreadyThere(quiet);
            } else {
                await setDoc(docRef, 
                    {
                        normal_data: {
                            [id]: dataObject
                        }
                    }, 
                { merge: true });
            }
        }
        console.log("Upload Successful");
        if (!quiet) Toast.success("Upload Successful to: " + eventName.substring(0, 4) + "" + eventName.substring(4) + " - " + dataObject["match_number"] + "-" + dataObject["alliance_color"] + dataObject["team_position"])
        return true;
    }
    catch (err) {
        console.log(err.message);
        if (!quiet) Toast.error("Upload failed")
    }
    return false;
}

function alreadyThere(quiet) {
    if (!quiet) Toast.info("Already in Firebase - not overwritten (edit it to replace it)");
    return 'exists';
}

export async function uploadTimekeepData(dataObject, gamemode, eventName, { quiet = false } = {}){
    const year = eventName.substring(0, 4);
    const eventCode = eventName.substring(4);
    const matchKey = "" + (parseInt(dataObject["match_number"]) + (gamemode + 1) * 1000) + "-" + dataObject["alliance_color"]
    const id = dataObject["scout_team"] + "-" + dataObject["scout_id"] + "-" + dataObject["alliance_color"] + dataObject["team_position"];

    try {
        if(year == 2026){
            const docRef = doc(
                db,
                "Competitions",
                "Years",
                year,
                eventCode,
                "Final",
                matchKey
            );
            await setDoc(docRef, 
                {
                    timekeeper_data: {
                        [id]: dataObject
                    }
                }, 
            { merge: true });
        }
        else{
            if (!quiet) Toast.error("Upload Failed, Wrong Year Active")
            return false;
        }
        if (!quiet) Toast.success("Timekeep Upload Successful to: " + eventName.substring(0, 4) + "" + eventName.substring(4) + " - " + dataObject["match_number"] + "-" + dataObject["alliance_color"] + dataObject["team_position"])
        return true;
    }
    catch (err) {
        console.log(err.message);
        if (!quiet) Toast.error("Timekeep Upload failed")
    }
    return false;
}
/**
 * Whoops this doesnt work bc firebase permisions dont allow reads to to this path
 * @param {*} authentication 
 * @returns 
 */
async function getVerifiedTeams(authentication){
    if(!authentication) {
        console.log("Failed to fetch firebase paths, not authorized");
        return false;
    }
    const path = "Allowed_Emails/0Verified";
    const docRef = getDoc(doc(db,path));
    const docData = (await docRef).data()
    for(const teamNumber in docData){
        console.log("Verified team: " + teamNumber )
    }
    console.log(docData);
}
export async function isTeamVerified(authentication, teamNumber) {
    if (!authentication) {
        console.log("Failed to fetch firebase paths, not authorized");
        return true;
    }
    if (!teamNumber) return false;
    try {
        const path = "Allowed_Emails/0Verified";
        const docRef = getDoc(doc(db, path));
        const docData = (await docRef).data().Teams;
        return docData.map(String).includes(String(teamNumber));
    } catch (error) {
        console.log("error checking verified teams", error);
        return false;
    }
}

/**
 * Gets the list of event paths in firebase for all years
 * @param {*} authentication 
 * @returns {Array<String>}array of the paths
 */
export async function getFirebaseEvents(authentication){
    const theEvents = new Map();
    if(!authentication) {
        console.log("Failed to fetch firebase paths, not authorized");
        return theEvents;
    }
    const years = ['2028', "2027", "2026", "2025", "2024","9999"];
    const path = "/Competitions/Years/";
    try{
        for (const theYear of years) {
            const collectionPath = "" + path + String(theYear);
            const collectionRef = collection(db, collectionPath);

            const querySnapshot = await getDocs(collectionRef);

            querySnapshot.forEach((doc) => {
                let data = doc.data()
                if(data.Name == undefined){
                } else{
                    let grandblue = String("" + String(theYear) + "" + doc.id)
                    theEvents.set(grandblue, data.Name);
                }
            });
        };
    } catch (err){
        console.log("Firebase all path fetching error: " + err);
    }
    return theEvents;
}

/**
 * For copying documents (mainly for moving round config files)
 * @param {*} sourcePath path for where the document is coming from
 * @param {*} targetPath path for where the document is going
 * @returns 
 */
export async function copyDocument(sourcePath, targetPath) {
    try {
        const sourceDocRef = doc(db, ...sourcePath.split("/"));
        const targetDocRef = doc(db, ...targetPath.split("/"));

        const sourceDoc = await getDoc(sourceDocRef);
        
        if (!sourceDoc.exists()) {
            console.log(`Document ${sourcePath} does not exist.`);
            return false;
        }

        await setDoc(targetDocRef, sourceDoc.data());

        console.log(`Document copied from ${sourcePath} to ${targetPath}`);
        return true;
    }
    catch (error) {
        console.error(`Error copying document from ${sourcePath} to ${targetPath}`, error);
        return false;
    }
}

export async function createDocument(year, eventCode, name){
    const unsortedDataRef = doc(
        db,
        "Competitions",
        "Years",
        year,
        eventCode
    );
    try{
        await setDoc(unsortedDataRef, {
                "Name": name
            }, { merge: true });
        return true;
    } catch (err) {
        console.log(err)
        return false
    }
}

export async function getAdminEmails(authorization){
    if(authorization === false){
        return false;
    }
    const adminListRef = doc(db, `Allowed_Emails/0AdminEmails`);
    try {
        const docSnap = await getDoc(adminListRef);
        if (docSnap.exists()) {
            const data = docSnap.data();
            const dataList = data.Emails;
            const adminList = [...dataList];
            return adminList;
        } else {
            return false;
        }
    } catch (error) {
        console.error("Error checking admin list:", error);
        return false;
    }
}

export async function autofillAccountInfo(authentication, setUser, setAuthentication){
    // Will not autofill at all if not signed in
    if(authentication === false){
        return {scout_id: "", scout_team:""};
    }

    return new Promise((resolve) => {
        onAuthStateChanged(auth, async (currentUser) => {
            if (!currentUser || !currentUser.email) {
                resolve({scout_id: "", scout_team: ""});
                return;
            }
            const docSnap = await getDoc(doc(db, 'Allowed_Emails', currentUser.email));
            if (docSnap.exists()) {
                const data = docSnap.data();
                resolve({scout_id: data.Name || "", scout_team: data.Team || ""});
            } else {
                resolve({scout_id: "", scout_team: ""});
            }
        });
    });
}

export async function isAdmin(authentication) {
    if (authentication === false) {
        return false;
    }

    const adminList = await getAdminEmails(authentication);
    if (adminList === false) {
        return false;
    }
    const currentUser = auth.currentUser;
    if (!currentUser || !currentUser.email) {
        return false;
    }

    //temp:
    //console.log(adminList);
    return adminList.includes(currentUser.email);
}

export async function resetPassword(email) {
    try {
        await sendPasswordResetEmail(auth, email);
        Toast.success("Reset email successfully sent. Check your email.");
    } catch (error) {
        Toast.error("Error sending password reset email:", error.message);
        alert(`Error: ${error.message}`);
    }
};